/** The right-click info card.
 *
 * The classic answers a right press with a panel that says more than the status bar
 * has room for: a hero's four primaries and his army, a town's garrison and
 * what it has built, a mine's yield and who holds it, a stack's rough size.
 * This build had nothing - the right button panned the map and that was all -
 * so the only way to find out what an enemy hero was carrying was to walk into
 * him.
 *
 * Pure, like `describe.ts` beside it, and for the same reason: the wording is
 * what is worth pinning, and a test that has to mount a canvas to ask "does a
 * remembered mine admit it is remembered" is a test nobody writes.
 *
 * ## What the card is allowed to say
 *
 * Exactly what the observation carries, and not one field more. `ObservedHero`
 * hands over `detail` for the player's own heroes and `null` for everybody
 * else's, and `ObservedTown` does the same with `garrison`. That is the fog
 * rule (I9) already enforced one layer down, so the card never has to decide
 * what an enemy may know - it renders what it was given and says "not from
 * here" where it was given nothing. A card that guessed would be a card that
 * leaked.
 */

import type {
  ArmyState, ContentRegistry, CreatureDefId, ObservedHero, ObservedObject,
  ObservedTown, PlayerObservation, TerrainKind,
} from "@game/core";
import { RESOURCES } from "@game/core";

import { formatNumber, humanise } from "./format.js";
import { colourFor } from "./minimap-paint.js";
import { objectDisplayName, objectEffect } from "./describe.js";
import { heroPortrait } from "../screens/art.js";
import { findHero, findObject, findTown, magicGroundAt, terrainAt } from "./select.js";
import { heroDisplayName } from "./hero-name.js";
import type { HoverTarget } from "./types.js";

export interface InfoRow {
  readonly label: string;
  readonly value: string;
}

export interface InfoCard {
  readonly title: string;
  /** One line under the title: what kind of thing this is. */
  readonly subtitle: string | null;
  /** An atlas key for the picture, or null for a card with no portrait. */
  readonly icon: string | null;
  /** Prose, one paragraph per entry. */
  readonly lines: readonly string[];
  /** A label/value table under the prose. */
  readonly rows: readonly InfoRow[];
  /** Quiet text at the foot: provenance, staleness, a caveat. */
  readonly footer: string | null;
}

const EMPTY: InfoCard = Object.freeze({
  title: "", subtitle: null, icon: null, lines: [], rows: [], footer: null,
});

// ------------------------------------------------------------------ armies

/**
 * The classic's stack sizes, which is what a player is shown of an army he does not
 * own. The exact count is a fact about somebody else's board; the band is what
 * scouting buys you.
 */
export const STACK_BANDS: readonly { readonly upTo: number; readonly word: string }[] =
  Object.freeze([
    { upTo: 4, word: "a few" },
    { upTo: 9, word: "several" },
    { upTo: 19, word: "a pack" },
    { upTo: 49, word: "lots" },
    { upTo: 99, word: "a horde" },
    { upTo: 249, word: "a throng" },
    { upTo: 499, word: "a swarm" },
    { upTo: 999, word: "zounds" },
    { upTo: Number.MAX_SAFE_INTEGER, word: "a legion" },
  ]);

export function stackBand(count: number): string {
  for (const b of STACK_BANDS) if (count <= b.upTo) return b.word;
  return "a legion";
}

/**
 * An army as rows. `exact` is the difference between your own army and
 * somebody else's: yours prints the count, theirs prints the band.
 */
export function armyRows(
  army: ArmyState | null, content: ContentRegistry | null, exact: boolean,
): readonly InfoRow[] {
  if (army === null) return [];
  const out: InfoRow[] = [];
  for (const slot of army.slots) {
    if (slot === null || slot.count <= 0) continue;
    const def = content?.creatures.get(slot.def as CreatureDefId);
    out.push({
      label: def?.namePlural ?? humanise(slot.def as string),
      value: exact ? formatNumber(slot.count) : stackBand(slot.count),
    });
  }
  return out;
}

// ------------------------------------------------------------------- cards

/**
 * A hero's speciality in one line.
 *
 * The content models a speciality as a `kind` and a `target` id rather than as
 * prose, so the sentence is assembled here - the same three lookups the hero
 * sheet does, because there is nowhere else they could come from. All 199
 * heroes carry one and all 199 `modifiers` arrays behind them are empty today
 * (STATUS.md), so this names the thing without promising it does anything yet.
 */
export function specialtyLine(
  specialty: { readonly kind: string; readonly target: string },
  content: ContentRegistry | null,
): string | null {
  if (content === null || specialty.target === "") return null;
  if (specialty.kind === "spell") {
    const d = content.spells.get(specialty.target as never);
    return `Specialises in ${d?.name ?? humanise(specialty.target)}.`;
  }
  if (specialty.kind === "creature") {
    const d = content.creatures.get(specialty.target as CreatureDefId);
    return `Specialises in ${d?.namePlural ?? humanise(specialty.target)}.`;
  }
  const d = content.skills.get(specialty.target as never);
  return `Specialises in ${d?.name ?? humanise(specialty.target)}.`;
}

function heroCard(
  obs: PlayerObservation, content: ContentRegistry | null, hero: ObservedHero,
): InfoCard {
  const own = obs.self !== null && hero.owner === obs.self.id;
  const d = hero.detail;
  const rows: InfoRow[] = [];
  const primary = hero.primary ?? d?.primary ?? null;
  if (primary !== null) {
    rows.push({ label: "Attack", value: String(primary.attack) });
    rows.push({ label: "Defense", value: String(primary.defense) });
    rows.push({ label: "Spell Power", value: String(primary.spellPower) });
    rows.push({ label: "Knowledge", value: String(primary.knowledge) });
  }
  if (d !== null) {
    rows.push({ label: "Movement", value: formatNumber(d.movementLeft) });
    rows.push({ label: "Mana", value: formatNumber(d.mana) });
  }
  rows.push(...armyRows(hero.army, content, own));

  const def = content?.heroes.get(hero.def as never);
  const specialty = def === undefined ? null : specialtyLine(def.specialty, content);
  return {
    title: heroDisplayName(hero, content),
    subtitle: own
      ? `Level ${String(hero.level)} ${def?.heroClass ?? "hero"}`
      : `Level ${String(hero.level)} ${def?.heroClass ?? "hero"} of ${colourFor(obs, hero.owner as string)}`,
    icon: def === undefined ? null : heroPortrait(def.portrait),
    lines: [
      ...(specialty === null ? [] : [specialty]),
      ...(d === null ? ["What this hero carries is not visible from here."] : []),
    ],
    rows,
    // A disguised hero reads as a lower level than he is; the observation has
    // already applied it, so the card must not promise the number is true.
    footer: own ? null : "Seen from the map. An enemy's sheet is not.",
  };
}

function townCard(
  obs: PlayerObservation, content: ContentRegistry | null, town: ObservedTown,
): InfoCard {
  const own = obs.self !== null && town.owner === obs.self.id;
  const d = town.detail;
  const rows: InfoRow[] = [];
  if (d !== null) {
    rows.push({ label: "Buildings", value: String(d.built.length) });
    if (d.visitingHero !== null) {
      const h = findHero(obs, d.visitingHero);
      rows.push({ label: "Visiting", value: h === null ? "a hero" : heroDisplayName(h, content) });
    }
  }
  rows.push(...armyRows(town.garrison, content, own));

  return {
    title: town.name,
    subtitle: `${humanise(town.faction)} town`,
    icon: `town.${town.faction}.background`,
    lines: town.garrison === null && !own
      ? ["What stands inside is not visible from here."]
      : [],
    rows,
    footer: town.owner === null
      ? "Held by nobody, or out of sight"
      : own ? "Yours" : `Held by ${colourFor(obs, town.owner as string)}`,
  };
}

function objectCard(
  obs: PlayerObservation, content: ContentRegistry | null, object: ObservedObject,
): InfoCard {
  const def = content?.mapObjects.get(object.def);
  const effect = objectEffect(object, def, content);
  const flaggable = object.kind === "mine" || object.kind === "dwelling";
  const owner = object.owner === null
    ? (flaggable ? "Unclaimed" : null)
    : (obs.self !== null && object.owner === obs.self.id
        ? "Yours"
        : `Held by ${colourFor(obs, object.owner as string)}`);

  return {
    title: objectDisplayName(object, def, content),
    subtitle: null,
    icon: `obj.${object.def}`,
    lines: effect === null ? [] : [effect],
    rows: owner === null ? [] : [{ label: "Owner", value: owner }],
    // The one thing the card knows that the picture cannot show: whether this
    // is what is there now or what was there last time anybody looked.
    footer: object.visible ? null : "Remembered, not currently in sight",
  };
}

/** A plain grass step, which every other ground's price is stated against. */
const BASE_STEP = 100;

/**
 * What the ground does to a hero, in the player's terms: how a step here
 * compares with grass, and whose armies are at home on it. The numbers are the
 * terrain def's own `moveCost`; the home rule is the kernel's native-terrain
 * cap (`offRoadTerrainCost`): an army made wholly of one faction pays no more
 * on that faction's ground than on grass. Pathfinding also cuts the penalty,
 * which is said once rather than computed for a hero the card was not given.
 */
export function groundLines(kind: string, content: ContentRegistry | null): readonly string[] {
  const t = content?.terrain.get(kind as TerrainKind);
  if (t === undefined) return [];
  if (t.impassable) return ["Impassable."];
  const out: string[] = [];
  if (t.water) {
    out.push("Open water: a boat is needed, and every step costs the same.");
  } else if (t.moveCost <= BASE_STEP) {
    out.push("Normal going: a step here costs no more than on grass.");
  } else {
    const word = t.moveCost > 150 ? "Heavy going" : t.moveCost > 100 ? "Slow going" : "Rough going";
    const more = t.moveCost >= 2 * BASE_STEP ? "twice"
      : t.moveCost >= 175 ? "three quarters more"
      : t.moveCost >= 150 ? "half again"
      : t.moveCost >= 125 ? "a quarter more" : "a little more";
    out.push(`${word}: a step here costs ${more} what it costs on grass (${String(t.moveCost)} for ${String(BASE_STEP)}). Pathfinding cuts the penalty.`);
    const home = content === null ? [] : [...content.factions.values()]
      .filter((f) => f.nativeTerrain === kind)
      .map((f) => f.name)
      .sort();
    if (home.length > 0) {
      const names = home.length === 1 ? home[0]! : `${home.slice(0, -1).join(", ")} and ${home[home.length - 1]!}`;
      out.push(`Home ground for ${names}: an army of theirs alone pays no more here than on grass.`);
    }
  }
  return out;
}

function cellCard(
  obs: PlayerObservation, content: ContentRegistry | null, x: number, y: number, z: number,
): InfoCard {
  const kind = terrainAt(obs, x, y, z);
  if (kind === null) {
    return { ...EMPTY, title: "Unexplored", lines: ["Nobody of yours has been here."] };
  }
  const i = (z * obs.map.height + y) * obs.map.width + x;
  const cost = obs.map.moveCost[i] ?? 0;
  const rows: InfoRow[] = [];
  if (cost > 0) rows.push({ label: "Movement", value: formatNumber(cost) });
  const road = obs.map.road[i] ?? 0;
  if (road > 0) rows.push({ label: "Road", value: "Yes" });
  const river = obs.map.river[i] ?? 0;
  if (river > 0) rows.push({ label: "River", value: "Yes" });

  const t = content?.terrain.get(kind as TerrainKind);
  const impassable = cost === 0 || t?.impassable === true;
  const lines: string[] = impassable ? ["Impassable."] : [...groundLines(kind, content)];
  if (!impassable && road > 0) lines.push("A road replaces the ground's cost for a step along it.");

  // Magic ground: named as the subtitle, and its effect spelled out from the
  // content, because a green wash over grass says nothing about what it does
  // to a fight - and it decides every spell cast there (Section 31).
  const groundId = magicGroundAt(obs, x, y, z);
  const ground = groundId === null ? null : content?.magicGrounds.get(groundId) ?? null;
  const groundName = groundId === null ? null : ground?.name ?? humanise(groundId);
  if (groundName !== null) {
    lines.push(ground?.description ?? `${groundName}: a magic ground that changes battles fought on it.`);
  }

  return {
    title: humanise(kind),
    subtitle: groundName ?? (t?.water === true ? "Open water" : null),
    icon: null,
    lines,
    rows,
    footer: groundName === null ? null : "Magic ground works only in a battle fought on it.",
  };
}

/**
 * The card for whatever the pointer is over.
 *
 * The same `HoverTarget` the status bar takes, so the map, the hero list and
 * the town list all reach the card through the machinery they already have.
 */
export function describeInfo(
  obs: PlayerObservation, content: ContentRegistry | null, target: HoverTarget,
): InfoCard {
  switch (target.kind) {
    case "NONE": return EMPTY;
    case "HERO": {
      const h = findHero(obs, target.id);
      return h === null ? EMPTY : heroCard(obs, content, h);
    }
    case "TOWN": {
      const t = findTown(obs, target.id);
      return t === null ? EMPTY : townCard(obs, content, t);
    }
    case "OBJECT": {
      const o = findObject(obs, target.id);
      return o === null ? EMPTY : objectCard(obs, content, o);
    }
    case "CELL": return cellCard(obs, content, target.x, target.y, target.z);
  }
}

/** Whether there is anything worth putting on screen. */
export const hasInfo = (card: InfoCard): boolean => card.title !== "";

// ------------------------------------------------------- the kingdom card

/**
 * The card behind a right press on the resource bar.
 *
 * The classic answers it with the day's income, which is a number the player
 * otherwise has to work out by adding up his towns and mines. The income is a
 * rule output, so the host supplies it rather than the shell deriving it.
 */
export function treasuryCard(
  obs: PlayerObservation, income: Partial<Record<string, number>> | null,
): InfoCard {
  const res = obs.resources;
  if (res === null) return EMPTY;
  const rows: InfoRow[] = RESOURCES.map((r) => {
    const per = income?.[r] ?? 0;
    return {
      label: humanise(r),
      value: per === 0
        ? formatNumber(res[r])
        : `${formatNumber(res[r])}  (${per > 0 ? "+" : ""}${formatNumber(per)} a day)`,
    };
  });
  return {
    title: "Treasury",
    subtitle: `Day ${String(obs.turn.day)}, week ${String(obs.turn.week)}, month ${String(obs.turn.month)}`,
    icon: null,
    lines: [],
    rows,
    footer: income === null ? "Income is counted at the day boundary." : null,
  };
}
